/**
 * Bubble-diagram layout presets (counts 2–7) and the editable diagram
 * override shape. Geometry is independent of workshop accent/copy —
 * consumers merge a preset with authored or overridden bubble text.
 */

import type { BubbleConfig } from "@/components/diagrams/types";

export type BubbleCount = 2 | 3 | 4 | 5 | 6 | 7;

export const BUBBLE_COUNTS: readonly BubbleCount[] = [2, 3, 4, 5, 6, 7];

/** Editable per-bubble copy (geometry comes from the count preset). */
export type DiagramBubbleContent = {
  id: string;
  titleLines: string[];
  bodyLines: string[];
  tipTitle?: string;
  tipBody?: string;
  /** HAW-style extras (ignored by generic workshop diagrams). */
  subtitle?: string;
  badge?: string;
  lucide?: "user" | "users" | "eye" | "bot";
};

export type DiagramOverride = {
  bubbleCount: BubbleCount;
  bubbles: DiagramBubbleContent[];
};

export type DiagramLayout = {
  viewBox: string;
  /** Centre points for each slot, length === bubbleCount. */
  positions: Array<{ cx: number; cy: number; r: number }>;
  connectors: Array<[number, number, number, number]>;
  /** True when the last slot is a larger centre bubble (5 and 7). */
  hasCentre: boolean;
};

type Pos = { cx: number; cy: number; r: number };

function connectorsFrom(positions: Pos[]): Array<[number, number, number, number]> {
  const lines: Array<[number, number, number, number]> = [];
  for (let i = 0; i < positions.length; i++) {
    for (let j = i + 1; j < positions.length; j++) {
      const a = positions[i]!;
      const b = positions[j]!;
      lines.push([a.cx, a.cy, b.cx, b.cy]);
    }
  }
  return lines;
}

/** Star connectors: every outer bubble → centre (last slot). */
function connectorsToCentre(positions: Pos[]): Array<[number, number, number, number]> {
  const centre = positions[positions.length - 1]!;
  return positions.slice(0, -1).map((p) => [p.cx, p.cy, centre.cx, centre.cy]);
}

/**
 * Fixed layouts in a ~480×340 canvas (HAW-compatible). Counts 5 and 7
 * place a larger centre bubble last.
 */
export function layoutForCount(count: BubbleCount): DiagramLayout {
  const R = 62;
  const RC = 78;

  switch (count) {
    case 2: {
      const positions: Pos[] = [
        { cx: 150, cy: 170, r: R },
        { cx: 330, cy: 170, r: R },
      ];
      return {
        viewBox: "40 40 400 260",
        positions,
        connectors: connectorsFrom(positions),
        hasCentre: false,
      };
    }
    case 3: {
      const positions: Pos[] = [
        { cx: 240, cy: 90, r: R },
        { cx: 130, cy: 250, r: R },
        { cx: 350, cy: 250, r: R },
      ];
      return {
        viewBox: "40 20 400 310",
        positions,
        connectors: connectorsFrom(positions),
        hasCentre: false,
      };
    }
    case 4: {
      const positions: Pos[] = [
        { cx: 140, cy: 100, r: R },
        { cx: 340, cy: 100, r: R },
        { cx: 140, cy: 260, r: R },
        { cx: 340, cy: 260, r: R },
      ];
      return {
        viewBox: "40 20 400 320",
        positions,
        connectors: connectorsFrom(positions),
        hasCentre: false,
      };
    }
    case 5: {
      // HAW-style: TL / TR / BL / BR + centre
      const positions: Pos[] = [
        { cx: 120, cy: 90, r: R },
        { cx: 360, cy: 90, r: R },
        { cx: 120, cy: 250, r: R },
        { cx: 360, cy: 250, r: R },
        { cx: 240, cy: 170, r: RC },
      ];
      return {
        viewBox: "40 20 400 310",
        positions,
        connectors: connectorsToCentre(positions),
        hasCentre: true,
      };
    }
    case 6: {
      const positions: Pos[] = [
        { cx: 120, cy: 100, r: R },
        { cx: 240, cy: 100, r: R },
        { cx: 360, cy: 100, r: R },
        { cx: 120, cy: 250, r: R },
        { cx: 240, cy: 250, r: R },
        { cx: 360, cy: 250, r: R },
      ];
      return {
        viewBox: "40 20 400 320",
        positions,
        connectors: connectorsFrom(positions),
        hasCentre: false,
      };
    }
    case 7: {
      const positions: Pos[] = [
        { cx: 240, cy: 70, r: R },
        { cx: 340, cy: 120, r: R },
        { cx: 340, cy: 230, r: R },
        { cx: 240, cy: 280, r: R },
        { cx: 140, cy: 230, r: R },
        { cx: 140, cy: 120, r: R },
        { cx: 240, cy: 175, r: RC },
      ];
      return {
        viewBox: "40 10 400 340",
        positions,
        connectors: connectorsToCentre(positions),
        hasCentre: true,
      };
    }
  }
}

const ACCENT_STROKES = [
  "var(--accent-true-blue)",
  "var(--accent-gold)",
  "var(--accent-peach)",
  "var(--accent-melon)",
  "var(--accent-sky)",
  "var(--accent-green)",
  "var(--accent-true-blue)",
] as const;

/** Empty bubble slots for a new preset (facilitator fills titles). */
export function emptyBubblesForCount(count: BubbleCount): DiagramBubbleContent[] {
  return Array.from({ length: count }, (_, i) => ({
    id: `b${i + 1}`,
    titleLines: [`BUBBLE ${i + 1}`],
    bodyLines: [""],
    tipTitle: `Bubble ${i + 1}`,
    tipBody: "",
  }));
}

/**
 * Build renderable {@link BubbleConfig}s from a diagram override + workshop
 * accent tokens. Icons are omitted (string lucide ids stay on the content
 * for HAW); generic diagrams rely on title/body text.
 */
export function bubbleConfigsFromDiagram(
  diagram: DiagramOverride,
  workshopId: string,
): { viewBox: string; connectors: Array<[number, number, number, number]>; bubbles: BubbleConfig[] } {
  const layout = layoutForCount(diagram.bubbleCount);
  const bubbles: BubbleConfig[] = layout.positions.map((pos, i) => {
    const content = diagram.bubbles[i] ?? emptyBubblesForCount(diagram.bubbleCount)[i]!;
    const isCentre = layout.hasCentre && i === layout.positions.length - 1;
    const r = pos.r;
    return {
      id: content.id || `b${i + 1}`,
      cx: pos.cx,
      cy: pos.cy,
      outerR: r,
      innerR: r - 5,
      fill: `url(#grad-${workshopId})`,
      stroke: ACCENT_STROKES[i % ACCENT_STROKES.length]!,
      outerStrokeW: 1.5,
      outerOp: 0.7,
      ringStrokeW: 1,
      ringOp: 0.4,
      filter: `url(#glow-${workshopId})`,
      iconTx: pos.cx - 9,
      iconScale: isCentre ? 0 : 0.75,
      titleFontSize: isCentre ? 12 : 10,
      letterSpacing: 0.5,
      dividerHW: isCentre ? 55 : 40,
      bodyFontSize: isCentre ? 11 : 10,
      titleLines: content.titleLines.length ? content.titleLines : [""],
      bodyLines: content.bodyLines,
      tipTitle: content.tipTitle,
      tipBody: content.tipBody,
    };
  });
  return { viewBox: layout.viewBox, connectors: layout.connectors, bubbles };
}

/** Resize an existing bubble list to a new count (keep copy, pad/truncate). */
export function resizeDiagramBubbles(
  bubbles: DiagramBubbleContent[],
  count: BubbleCount,
): DiagramBubbleContent[] {
  const next = bubbles.slice(0, count).map((b, i) => ({ ...b, id: b.id || `b${i + 1}` }));
  while (next.length < count) {
    const i = next.length;
    next.push({
      id: `b${i + 1}`,
      titleLines: [`BUBBLE ${i + 1}`],
      bodyLines: [""],
      tipTitle: `Bubble ${i + 1}`,
      tipBody: "",
    });
  }
  return next;
}
